import type { CSSProperties } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, Trophy, Loader2, ArrowLeft } from 'lucide-react';
import { useRanking } from "@/hooks/useRanking"
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeProvider';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';

export const Ranking = ({ isAdmin = false, showHeading = true }: { isAdmin?: boolean; showHeading?: boolean }) => {
    const { data: leaderboard } = useRanking();
    const { t } = useTranslation();
    const { tokens } = useTheme();
    const [loading, setLoading] = useState(true);
    const backgroundSurface = tokens.colors.background || '0 0% 100%';
    const backgroundText = tokens.colors.foreground || tokens.colors['card-foreground'];
    const cardSurface = tokens.colors.card || backgroundSurface;
    const cardText = tokens.colors['card-foreground'] || backgroundText;
    const primarySurface = tokens.colors.primary || cardSurface;
    const primaryText = tokens.colors['primary-foreground'] || cardText;

    const panelColors = useMemo(
        () => getSurfaceContrastColors(isAdmin ? cardSurface : backgroundSurface, isAdmin ? cardText : backgroundText),
        [backgroundSurface, backgroundText, cardSurface, cardText, isAdmin],
    );
    const rowColors = useMemo(
        () => getSurfaceContrastColors(cardSurface, cardText),
        [cardSurface, cardText],
    );
    const pillColors = useMemo(
        () => getSurfaceContrastColors(primarySurface, primaryText),
        [primarySurface, primaryText],
    );

    const panelSurfaceStyle = useMemo<CSSProperties>(
        () => toSurfaceContrastVariables(panelColors) as CSSProperties,
        [panelColors],
    );
    const rowSurfaceStyle = useMemo<CSSProperties>(
        () => toSurfaceContrastVariables(rowColors) as CSSProperties,
        [rowColors],
    );
    const pillSurfaceStyle = useMemo<CSSProperties>(
        () => toSurfaceContrastVariables(pillColors) as CSSProperties,
        [pillColors],
    );
    const headingTopOffsetStyle = useMemo<CSSProperties>(
        () => ({ paddingTop: 'var(--heading-top-offset, 0px)' }),
        [],
    );

    useEffect(() => {
        if (leaderboard) {
            setLoading(false)
        }
    }, [leaderboard])


    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const goToUser = (user) => {
        return `/usuario?userId=${user.user_id}&sortBy=votes`
    }

    return (<Card
        className={`${!isAdmin && "relative z-10 rounded-2xl bg-background p-4 md:p-8 shadow-xl animate-breathing"} text-[hsl(var(--surface-fg))]`}
        style={panelSurfaceStyle}
    >
        <CardHeader className="text-center pb-4">
            {showHeading &&
                <div className="flex flex-col items-center space-x-2 mb-2">
                    <div className="flex items-center space-x-2">
                        <Trophy className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
                        <div style={headingTopOffsetStyle}>
                            <CardTitle className="text-2xl md:text-4xl">{t('rankingTitle')}</CardTitle>
                        </div>
                    </div>
                    <p className="text-[hsl(var(--surface-muted-fg))]">Invitados con más likes a través de sus fotos</p>

                </div>
            }

        </CardHeader>
        <CardContent className="px-2 md:px-6">
            {leaderboard.length > 0 ? (
                <div className="space-y-3">
                    {leaderboard.map((user, index) => (
                        <Link
                            key={user.user_id}
                            to={`${isAdmin && goToUser(user)}`}
                            className={`block ${!isAdmin && "pointer-events-none"}`}
                        >
                            <div
                                key={user.user_id}
                                className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border bg-card text-[hsl(var(--surface-fg))]"
                                style={rowSurfaceStyle}
                            >
                                <div
                                    className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-sm bg-primary font-bold text-xs md:text-sm text-[hsl(var(--surface-fg))]"
                                    style={pillSurfaceStyle}
                                >
                                    {index + 1}
                                </div>
                                <Avatar className="w-10 h-10 md:w-12 md:h-12">
                                    <AvatarImage
                                        src={user?.avatar_url}
                                        alt={user?.display_name || 'Usuario'}
                                    />
                                    <AvatarFallback>
                                        {(user.display_name || 'U').charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm md:text-lg truncate text-[hsl(var(--surface-fg))]">
                                        {user?.display_name || 'Un invitado'}
                                    </div>
                                    <div className="text-xs md:text-sm text-[hsl(var(--surface-muted-fg))]">
                                        {t('votedPhotosByUser', { count: user.voted_photos_count })}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                                    <Heart className={`w-3 h-3 md:w-4 md:h-4 fill-red-500 text-red-500`} />
                                    <span className="font-bold text-lg md:text-xl text-[hsl(var(--surface-fg))]">{user.total_votes}</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 text-[hsl(var(--surface-muted-fg))]">
                    <Trophy className="w-16 h-16 mx-auto mb-4 text-[hsl(var(--surface-muted-fg))]" />

                    <p className="text-lg text-[hsl(var(--surface-fg))]">{t('noUsersInRanking')}</p>
                </div>
            )}
        </CardContent>
    </Card >


    );
};

export default Ranking;
